import { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import i18n from "i18next";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="flex flex-col items-center gap-5 max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-red-400" />
          </div>
          <div className="space-y-2">
            <h1 className="text-lg font-semibold text-foreground">
              {i18n.t("common:errorBoundary.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {i18n.t("common:errorBoundary.description")}
            </p>
          </div>
          {this.state.error && (
            <div className="w-full rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3 text-left">
              <p className="text-[11px] font-mono text-red-400/70 break-all">
                {this.state.error.message}
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 text-[12px] font-medium rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] active:bg-white/[0.08] text-foreground transition-colors"
            >
              {i18n.t("common:errorBoundary.tryAgain")}
            </button>
            <button
              onClick={this.handleReload}
              className="px-4 py-2 text-[12px] font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 transition-colors flex items-center gap-1.5"
            >
              <RefreshCw className="w-3 h-3" />
              {i18n.t("common:errorBoundary.reloadApp")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
